import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { FileSpreadsheet, FileText, Save, Trash2, BarChart3, PieChart as PieIcon, Layers3 } from "lucide-react";
import api, { API, apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PALETTE = ["#1F4E79", "#1f8a4c", "#e6a100", "#c62828", "#5b6bbf", "#0f8f9e", "#8d5fbf", "#c2703d"];
const RAG_COLORS = { Red: "#c62828", Amber: "#e6a100", Green: "#1f8a4c" };
const METRICS = [
  { value: "count", label: "Count" },
  { value: "sum_percent_complete", label: "Sum of % complete" },
  { value: "avg_percent_complete", label: "Average % complete" },
  { value: "sum_cost_impact", label: "Sum of cost impact" },
];
const CHARTS = [
  { value: "column", label: "Column", icon: BarChart3 },
  { value: "cluster", label: "Clustered column", icon: Layers3 },
  { value: "pie", label: "Pie", icon: PieIcon },
];

const pretty = (s) =>
  (s || "")
    .replace(/_id$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bRag\b/g, "RAG");

export default function Reports() {
  const [sources, setSources] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [clients, setClients] = useState([]);
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [data, setData] = useState({ rows: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({
    source: "projects", dimension: "rag", series: "", metric: "count",
    chart_type: "column", portfolio_id: "", programme_id: "", client_id: "",
  });

  const loadViews = useCallback(() => api.get("/reports/views").then(({ data }) => setViews(data)), []);

  useEffect(() => {
    api.get("/reports/sources").then(({ data }) => setSources(data));
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
    api.get("/programmes").then(({ data }) => setProgrammes(data));
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
    loadViews();
  }, [loadViews]);

  const run = useCallback(async (config) => {
    setLoading(true);
    try {
      const payload = { ...config, series: config.series || null, portfolio_id: config.portfolio_id || null, programme_id: config.programme_id || null, client_id: config.client_id || null };
      if (payload.chart_type === "pie") payload.series = null;
      const { data } = await api.post("/reports/data", payload);
      setData(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (sources.length) run(cfg); }, [sources.length, cfg, run]);

  const dims = sources.find((s) => s.source === cfg.source)?.dimensions || [];

  const setField = (k) => (v) =>
    setCfg((c) => {
      const next = { ...c, [k]: v };
      if (k === "source") {
        const list = sources.find((s) => s.source === v)?.dimensions || [];
        next.dimension = list[0] || "";
        next.series = "";
      }
      if (k === "chart_type" && v !== "cluster") next.series = "";
      return next;
    });

  const exportAs = async (fmt) => {
    try {
      const payload = { ...cfg, series: cfg.chart_type === "cluster" ? cfg.series || null : null, portfolio_id: cfg.portfolio_id || null, programme_id: cfg.programme_id || null, client_id: cfg.client_id || null };
      const res = await api.post(`/reports/export/${fmt}`, payload, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cfg.source}-by-${cfg.dimension}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported to ${fmt === "excel" ? "Excel" : "PDF"}`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  const saveView = async () => {
    if (!viewName.trim()) return toast.error("Give the view a name");
    try {
      await api.post("/reports/views", {
        name: viewName,
        config: { ...cfg, series: cfg.series || null, portfolio_id: cfg.portfolio_id || null, programme_id: cfg.programme_id || null, client_id: cfg.client_id || null },
      });
      setViewName("");
      await loadViews();
      toast.success("View saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const applyView = (v) => {
    setCfg({
      source: v.config.source, dimension: v.config.dimension, series: v.config.series || "",
      metric: v.config.metric, chart_type: v.config.chart_type,
      portfolio_id: v.config.portfolio_id || "", programme_id: v.config.programme_id || "",
      client_id: v.config.client_id || "",
    });
    toast.success(`Loaded “${v.name}”`);
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete saved view “${v.name}”?`)) return;
    await api.delete(`/reports/views/${v.id}`);
    await loadViews();
  };

  const colourFor = (key, i) => RAG_COLORS[key] || PALETTE[i % PALETTE.length];
  const pieData = data.rows.map((r) => ({ name: r.label, value: r[data.series[0]] ?? 0 }));

  return (
    <Shell
      testId="reports-page"
      title="Report Builder"
      subtitle="Build a view, switch the chart type, then export or save it"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-excel-btn" onClick={() => exportAs("excel")} className="gap-1.5">
            <FileSpreadsheet size={15} /> Excel
          </Button>
          <Button variant="outline" data-testid="export-pdf-btn" onClick={() => exportAs("pdf")} className="gap-1.5">
            <FileText size={15} /> PDF
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
        <div className="space-y-4 xl:col-span-1">
          <div className="panel p-5" data-testid="report-config-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Configuration</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="field-label">Data Source</label>
                <Select testId="report-source-select" value={cfg.source} onChange={setField("source")} options={sources.map((s) => ({ value: s.source, label: pretty(s.source) }))} placeholder="Select source" />
              </div>
              <div>
                <label className="field-label">Group By</label>
                <Select testId="report-dimension-select" value={cfg.dimension} onChange={setField("dimension")} options={dims.map((d) => ({ value: d, label: pretty(d.replace("_id", "")) }))} placeholder="Select dimension" />
              </div>
              <div>
                <label className="field-label">Metric</label>
                <Select testId="report-metric-select" value={cfg.metric} onChange={setField("metric")} options={METRICS} placeholder="Select metric" />
              </div>
              <div>
                <label className="field-label">Chart Type</label>
                <Select testId="report-chart-select" value={cfg.chart_type} onChange={setField("chart_type")} options={CHARTS.map((c) => ({ value: c.value, label: c.label }))} placeholder="Select chart" />
              </div>
              {cfg.chart_type === "cluster" && (
                <div>
                  <label className="field-label">Cluster By</label>
                  <Select testId="report-series-select" value={cfg.series} onChange={setField("series")} options={dims.filter((d) => d !== cfg.dimension).map((d) => ({ value: d, label: pretty(d.replace("_id", "")) }))} placeholder="Select series" />
                </div>
              )}
              <div>
                <label className="field-label">Portfolio Filter</label>
                <Select testId="report-portfolio-filter" value={cfg.portfolio_id} onChange={setField("portfolio_id")} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="All portfolios" />
              </div>
              <div>
                <label className="field-label">Programme Filter</label>
                <Select testId="report-programme-filter" value={cfg.programme_id} onChange={setField("programme_id")} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="All programmes" />
              </div>
              {clients.length > 0 && (
                <div>
                  <label className="field-label">Client Filter</label>
                  <Select testId="report-client-filter" value={cfg.client_id} onChange={setField("client_id")} options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="All clients" />
                </div>
              )}
            </div>
          </div>

          <div className="panel p-5" data-testid="saved-views-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Saved Views</h3>
            <div className="mt-3 flex gap-2">
              <Input data-testid="view-name-input" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="View name" />
              <Button data-testid="save-view-btn" onClick={saveView} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
                <Save size={14} /> Save
              </Button>
            </div>
            <div className="mt-3 divide-y divide-slate-100">
              {views.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">No saved views yet.</p>
              ) : (
                views.map((v) => (
                  <div key={v.id} data-testid={`saved-view-${v.id}`} className="flex items-center justify-between gap-2 py-2">
                    <button onClick={() => applyView(v)} data-testid={`load-view-${v.id}`} className="min-w-0 flex-1 text-left">
                      <div className="truncate text-sm font-medium text-slate-800">{v.name}</div>
                      <div className="text-xs text-slate-400">{pretty(v.config.source)} · {pretty(v.config.chart_type)}</div>
                    </button>
                    <button data-testid={`delete-view-${v.id}`} onClick={() => deleteView(v)} aria-label="Delete saved view" title="Delete view" className="text-slate-400 transition-colors hover:text-red-600">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6 xl:col-span-3">
          <div className="panel p-5" data-testid="report-chart-panel">
            <div className="mb-4 flex items-baseline justify-between gap-4">
              <h3 className="font-display text-base font-semibold text-slate-800">
                {pretty(cfg.source)} by {pretty(cfg.dimension.replace("_id", ""))}
              </h3>
              <span className="text-xs uppercase tracking-wider text-slate-400">
                {METRICS.find((m) => m.value === cfg.metric)?.label}
              </span>
            </div>
            {loading ? (
              <p className="py-24 text-center text-sm text-slate-400">Building report…</p>
            ) : data.rows.length === 0 ? (
              <Empty testId="report-empty" text="No data matches this configuration." />
            ) : cfg.chart_type === "pie" ? (
              <ResponsiveContainer width="100%" height={380}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={130} paddingAngle={2} label>
                    {pieData.map((p, i) => <Cell key={p.name} fill={colourFor(p.name, i)} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={data.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf1" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {data.series.map((s, i) => (
                    <Bar key={s} dataKey={s} name={s === "Value" ? METRICS.find((m) => m.value === cfg.metric)?.label : s}
                      fill={colourFor(s, i)} stackId={cfg.chart_type === "column" ? "a" : undefined} radius={[3, 3, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="panel overflow-x-auto p-5" data-testid="report-table-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Underlying Data</h3>
            <table className="mt-3 w-full min-w-[420px] text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="pb-2 pr-3 font-semibold">Group</th>
                  {data.series.map((s) => <th key={s} className="pb-2 pr-3 font-semibold">{s}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.label} data-testid={`report-row-${r.label}`}>
                    <td className="py-2 pr-3 font-medium text-slate-800">{r.label}</td>
                    {data.series.map((s) => <td key={s} className="py-2 pr-3 text-slate-600">{r[s] ?? 0}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Shell>
  );
}
